"use client"

import type React from "react"

import { Card, CardBody, Col, Form, FormGroup, Input, Label, Media, Row } from "reactstrap"
import { H4, H6, P } from "../../../../../AbstractElements"
import { dynamicImage } from "../../../../../Service"
import { useState, useEffect, useRef } from "react"
import { ProfileService, type UserProfile } from "../../../../../Service/ProfileService"
import { toast } from "react-toastify"
import CommonCardHeader from "../../../../../CommonElements/CommonCardHeader/CommonCardHeader"
import { FaCamera, FaUser } from "react-icons/fa"

const EditMyProfile = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Fetch profile data
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true)
        const profileData = await ProfileService.getProfile()
        setProfile(profileData)
      } catch (error) {
        console.error("Failed to fetch profile:", error)
        toast.error("Failed to load profile data")
      } finally {
        setLoading(false)
      }
    }

    fetchProfile()
  }, [])

  const handlePhotoClick = () => {
    fileInputRef.current?.click()
  }

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size must be less than 5MB")
        return
      }

      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Please select an image file")
        return
      }

      try {
        setUploading(true)
        const base64 = await ProfileService.fileToBase64(file)

        const response = await ProfileService.updateProfile({
          photo_base64: base64,
        })

        setProfile(response.profile)
        toast.success("Profile photo updated successfully!")
      } catch (error: any) {
        console.error("Failed to upload photo:", error)
        toast.error(error.response?.data?.error || "Failed to upload photo")
      } finally {
        setUploading(false)
      }
    }
  }

  const getDisplayName = () => {
    if (!profile?.person) return "User"
    return profile.person.full_name || `${profile.person.first_name} ${profile.person.last_name}`.trim() || "User"
  }

  const getRoleName = () => {
    if (profile?.is_superuser) return "Super Admin"
    if (profile?.is_staff) return "Staff"
    return "User"
  }

  const getProfileImage = () => {
    if (profile?.person?.photo_path) {
      return profile.person.photo_path
    }
    return dynamicImage("user/7.jpg") || "/placeholder.svg?height=70&width=70"
  }

  if (loading) {
    return (
      <Col xl="4">
        <Card>
          <CardBody className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading profile...</p>
          </CardBody>
        </Card>
      </Col>
    )
  }

  return (
    <Col xl="4">
      <Card>
        <CommonCardHeader title="My Profile" tagClass="card-title mb-0" />
        <CardBody>
          <Form onSubmit={(event) => event.preventDefault()}>
            <Row className="mb-2">
              <div className="profile-title">
                <Media>
                  <div className="position-relative">
                    {/* Use regular img tag instead of Image component for onClick support */}
                    <img
                      className="img-70 rounded-circle"
                      alt="profile-user"
                      src={getProfileImage() || "/placeholder.svg"}
                      style={{ cursor: "pointer", objectFit: "cover" }}
                      onClick={handlePhotoClick}
                    />
                    <div
                      className="position-absolute bottom-0 end-0 bg-primary rounded-circle p-1"
                      style={{ cursor: "pointer", width: "24px", height: "24px" }}
                      onClick={handlePhotoClick}
                    >
                      {uploading ? (
                        <div
                          className="spinner-border spinner-border-sm text-white"
                          style={{ width: "12px", height: "12px" }}
                        >
                          <span className="sr-only">Uploading...</span>
                        </div>
                      ) : (
                        <FaCamera size={10} className="text-white" />
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      style={{ display: "none" }}
                    />
                  </div>
                  <Media body className="ms-3">
                    <H4 className="mb-1">{getDisplayName()}</H4>
                    <P className="text-muted">{getRoleName()}</P>
                    {profile?.person?.phone && <small className="text-muted d-block">📞 {profile.person.phone}</small>}
                  </Media>
                </Media>
              </div>
            </Row>

            {/* Profile Status */}
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                <div>
                  <H6 className="mb-1">Profile Status</H6>
                  <small className="text-muted">
                    {profile?.person?.id_doc_number ? "Profile Complete" : "Profile Incomplete"}
                  </small>
                </div>
                <div>
                  {profile?.person?.id_doc_number ? (
                    <span className="badge bg-success">✓ Complete</span>
                  ) : (
                    <span className="badge bg-warning">⚠ Incomplete</span>
                  )}
                </div>
              </div>
            </div>

            {/* Profile Information */}
            <FormGroup>
              <Label>Email Address</Label>
              <Input value={profile?.email || ""} disabled className="bg-light text-dark" />
            </FormGroup>

            {profile?.person?.id_doc_type_name && (
              <FormGroup>
                <Label>ID Document Type</Label>
                <Input value={profile.person.id_doc_type_name} disabled className="bg-light text-dark" />
              </FormGroup>
            )}

            {profile?.person?.id_doc_number && (
              <FormGroup>
                <Label>ID Document Number</Label>
                <Input value={profile.person.id_doc_number} disabled className="bg-light text-dark" />
              </FormGroup>
            )}

            {!profile?.person?.id_doc_number && (
              <div className="alert alert-warning">
                <FaUser className="me-2" />
                <strong>Complete your profile!</strong>
                <br />
                Please fill in your identification information to complete your profile setup.
              </div>
            )}

            <div className="form-footer">
              <small className="text-muted d-block text-center">
                Last updated:{" "}
                {profile?.person?.updated_at ? new Date(profile.person.updated_at).toLocaleDateString() : "Never"}
              </small>
            </div>
          </Form>
        </CardBody>
      </Card>
    </Col>
  )
}

export default EditMyProfile
